import React from 'react'
import {useLocation} from 'react-router-dom'
import {Box, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import FaqList from './faq-list'
import ReadMore from '@app/components/read-more'
import BottomLinkedCategories from './bottom-linked-categories'
import PropTypes from 'prop-types'
import useSiteType from '../../../hooks/use-site-type'

const getFaqData = (category = {}) => {
    return [
        {
            question: category?.c_faqQuestion1,
            answer: category?.c_faqAnswer1
        },
        {
            question: category?.c_faqQuestion2,
            answer: category?.c_faqAnswer2
        },
        {
            question: category?.c_faqQuestion3,
            answer: category?.c_faqAnswer3
        },
        {
            question: category?.c_faqQuestion4,
            answer: category?.c_faqAnswer4
        },
        {
            question: category?.c_faqQuestion5,
            answer: category?.c_faqAnswer5
        }
    ].filter((item) => item.question && item.answer)
}

const isPaginatedPLP = (search = '') => {
    const searchParams = new URLSearchParams(search)
    const pParam = searchParams.get('p')

    return Number(pParam) > 1
}

/**
 * BottomElements renders the bottom section of a product list page.
 *
 * The SEO-related bottom blocks must only render on the canonical PLP page:
 * - bottom description
 * - FAQ
 * - bottom linked categories
 *
 * They must be not in the DOM on paginated URLs like ?p=2 or ?p=3.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.category - The category object containing data for rendering the bottom elements.
 * @param {string} [props.category.c_bottomPLPLayoutID] - The ID of the bottom PLP layout to be used.
 * @param {string} [props.category.c_bottomDescription] - The description text to be displayed at the bottom.
 * @param {Array} [props.category.c_bottomLinkedCategories] - An array of bottom linked categories to be displayed.
 *
 * @returns {JSX.Element} The rendered bottom elements section.
 */
const BottomElements = ({category = {}}) => {
    const location = useLocation()
    const {isOutlet} = useSiteType()
    const isPaginatedPage = isPaginatedPLP(location.search)

    const bottomLinkedCategories = category?.c_bottomLinkedCategories
    const faqData = getFaqData(category)

    const hasBottomDescription = Boolean(category?.c_bottomShortDescription)
    const hasFaqData = faqData.length > 0
    const hasBottomLinkedCategories = bottomLinkedCategories?.length > 0
    const hasBottomContent = hasBottomDescription || hasFaqData || hasBottomLinkedCategories

    const shouldRenderCanonicalBlocks = hasBottomContent && !isPaginatedPage && !isOutlet

    if (!shouldRenderCanonicalBlocks) {
        return null
    }

    return (
        <Box as="section">
            <Stack
                as="section"
                px={[10, 10, 45, 0]}
                py={[63, 63, 70, 70]}
                gap="20px"
                maxWidth={{base: '100%', lg: '960px'}}
                margin="auto"
            >
                {/* Footer bottom description */}
                {hasBottomDescription && (
                    <ReadMore
                        initialSeoText={category?.c_bottomShortDescription}
                        secondarySeoText={category?.c_bottomDescription}
                        showReadLess={true}
                        containerProps={{
                            marginTop: '0',
                            textAlign: 'center'
                        }}
                        textProps={{
                            as: 'div',
                            display: 'block',
                            fontSize: '12px',
                            padding: '0'
                        }}
                        buttonProps={{
                            margin: '20px auto 0'
                        }}
                    />
                )}

                {/* FAQ section */}
                {hasFaqData && <FaqList faqData={faqData} />}

                {/* Levels 1-2: manually configured links only; level 3+: sister categories and manually configured links */}
                {hasBottomLinkedCategories && (
                    <BottomLinkedCategories categories={bottomLinkedCategories} />
                )}
            </Stack>
        </Box>
    )
}

BottomElements.propTypes = {
    category: PropTypes.object
}

export default BottomElements
